import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { ChartCard, EmptyChart } from '../charts/ChartPrimitives';
import {
  formatUnits,
  formatRate,
  formatDaysOfCover,
  daysOfCoverBand,
  RAG_TEXT,
  RAG_PILL,
} from '../../lib/reporting-format';

export type ReorderSignalRow = {
  skuId: string;
  sku: string;
  category: string;
  productType: string;
  onHand: number;
  notifyThreshold: number | null;
  runRate: number;
  daysOfCover: number | null;
  belowThreshold: boolean;
  reasons: string[];
};

interface ReorderSignalsCardProps {
  loading: boolean;
  rows: ReorderSignalRow[];
}

/**
 * The actionable payoff: every SKU that needs reordering, ranked by urgency
 * (days-of-cover ascending, then below-threshold first). Works for both POD
 * blanks and PAP finished goods.
 */
export const ReorderSignalsCard: React.FC<ReorderSignalsCardProps> = ({ loading, rows }) => {
  const { t } = useTranslation('inventory');

  const sorted = React.useMemo(() => {
    return [...rows].sort((a, b) => {
      const av = a.daysOfCover ?? Number.POSITIVE_INFINITY;
      const bv = b.daysOfCover ?? Number.POSITIVE_INFINITY;
      if (av !== bv) return av - bv;
      if (a.belowThreshold !== b.belowThreshold) return a.belowThreshold ? -1 : 1;
      return b.runRate - a.runRate;
    });
  }, [rows]);

  return (
    <ChartCard
      title={t('reporting.v2.reorderSignals', 'Reorder signals')}
      description={t(
        'reporting.v2.reorderSignalsDesc',
        'SKUs below threshold or running low on cover. Most urgent first.',
      )}
      action={
        sorted.length > 0 ? (
          <span className="text-xs font-medium text-muted-foreground tabular-nums">
            {t('reporting.v2.signalCount', '{{n}} flagged', { n: sorted.length })}
          </span>
        ) : undefined
      }
    >
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-10 animate-pulse rounded bg-muted/40" />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <EmptyChart
          message={t(
            'reporting.v2.noReorderSignals',
            'Nothing needs reordering. Every tracked SKU is above its threshold with healthy cover.',
          )}
        />
      ) : (
        <div className="max-h-[28rem] overflow-auto rounded-md border">
          <table className="w-full min-w-[48rem] border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="border-b text-left text-xs font-medium text-muted-foreground">
                <th className="py-2 pl-3 pr-3 font-medium">{t('reporting.v2.sku', 'SKU')}</th>
                <th className="py-2 px-3 font-medium">{t('reporting.v2.type', 'Type')}</th>
                <th className="py-2 px-3 text-right font-medium">
                  {t('reporting.v2.onHandThreshold', 'On hand / threshold')}
                </th>
                <th className="py-2 px-3 text-right font-medium">
                  {t('reporting.v2.runRate', 'Run-rate/day')}
                </th>
                <th className="py-2 px-3 text-right font-medium">
                  {t('reporting.v2.cover', 'Cover')}
                </th>
                <th className="py-2 pl-3 pr-3 font-medium">{t('reporting.v2.reasons', 'Reasons')}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => {
                const band = daysOfCoverBand(row.daysOfCover);
                return (
                  <tr key={`${row.skuId}-${row.sku}`} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pl-3 pr-3">
                      <p className="font-mono text-xs font-medium">{row.sku}</p>
                      <p className="text-xs text-muted-foreground">{row.category}</p>
                    </td>
                    <td className="py-2 px-3">
                      <span className="text-xs text-muted-foreground">{row.productType}</span>
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums">
                      <span className={cn(row.belowThreshold && 'font-semibold text-red-600')}>
                        {formatUnits(row.onHand)}
                      </span>
                      {row.notifyThreshold != null && (
                        <span className="text-muted-foreground">
                          {' / '}
                          {formatUnits(row.notifyThreshold)}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">
                      {formatRate(row.runRate)}
                    </td>
                    <td
                      className={cn(
                        'py-2 px-3 text-right font-semibold tabular-nums',
                        RAG_TEXT[band],
                      )}
                    >
                      {formatDaysOfCover(row.daysOfCover)}
                    </td>
                    <td className="py-2 pl-3 pr-3">
                      <div className="flex flex-wrap gap-1">
                        {row.reasons.length === 0 ? (
                          <span className="text-xs text-muted-foreground">-</span>
                        ) : (
                          row.reasons.map((reason) => (
                            <span
                              key={reason}
                              className={cn(
                                'rounded-md px-1.5 py-0.5 text-[10px] font-medium',
                                RAG_PILL.attention,
                              )}
                            >
                              {reason}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </ChartCard>
  );
};
